/**
 * 성분(몰%) 목록에서 혼합가스의 평균 물성을 구한다.
 *
 * ⚠ 표준식이 아니다 — 일반 이상기체 혼합 규칙이다.
 *   분자량 : MW = Σ(yᵢ·MWᵢ)                         — 몰분율 가중, 정확
 *   비열   : Cp(질량기준) = Σ(yᵢ·Cpᵢ,molar) / MW      — 이상기체는 질량당
 *            비열이 성분별 몰기준 비열의 몰분율 가중합을 MW_mix 로 나눈 것과
 *            정확히 같다(이상기체 비열은 순수 상태량이라 더해진다)
 *   비열비 : k = Cp_molar,mix / (Cp_molar,mix − R)    — R=8314.46 J/(kmol·K)
 *   점도   : Herning–Zipperer, μ = Σ(yᵢ√MWᵢ·μᵢ) / Σ(yᵢ√MWᵢ)
 *            (`isothermalFlow.ts` `mixStreams()` 와 같은 식 — 성분 단위로
 *            다시 구현한다. 두 모듈이 서로 의존하지 않게 하기 위해서다.)
 */
import { findGasComponent, type GasComponent } from "../data/gasComponents";
import { mixExplosiveLimits } from "./explosiveLimits";

/** R — isothermalFlow.ts 의 UNIVERSAL_GAS_CONSTANT 와 같은 값. */
const R_UNIVERSAL = 8314.46;

export interface MixedGasProperties {
  readonly molecularWeight: number;
  /** 질량기준 정압비열 (J/(kg·K)) */
  readonly specificHeatJKgK: number;
  readonly specificHeatRatio: number;
  readonly viscosityCp: number;
  /** 입력 성분 유량의 합 (kg/h) */
  readonly totalMassFlowKgH: number;
}

/**
 * 성분별 질량유량(kg/h) 딕셔너리에서 혼합 물성을 구한다.
 * 알 수 없는 성분 key 가 있거나 유량 합이 0 이면 null.
 *
 * `customDefs` — 표준 성분 데이터베이스(`gasComponents.ts`)에 없는 key를
 * 임시로 풀이할 때 쓴다("평균값 직접 입력" 소스 노드가 스스로를 성분
 * 하나처럼 취급하기 위해 노드별로 만드는 가상 성분 — `flowsheetState.ts`
 * 참고). 같은 key 가 `customDefs` 와 표준 DB 양쪽에 있으면 `customDefs`
 * 가 우선한다.
 */
export function mixComponentProperties(
  components: Readonly<Record<string, number>>,
  customDefs?: Readonly<Record<string, GasComponent>>,
): MixedGasProperties | null {
  let totalMass = 0;
  let totalMoles = 0;
  let molarCpSum = 0; // Σ(nᵢ·Cpᵢ,molar)
  let viscNum = 0; // Herning–Zipperer 분자: Σ(nᵢ√MWᵢ·μᵢ)
  let viscDen = 0; // Herning–Zipperer 분모: Σ(nᵢ√MWᵢ)

  for (const [key, massFlowKgH] of Object.entries(components)) {
    if (!Number.isFinite(massFlowKgH) || massFlowKgH <= 0) continue;
    const c = customDefs?.[key] ?? findGasComponent(key);
    if (c === null || c === undefined) return null;

    const moles = massFlowKgH / c.molecularWeight;
    const weight = moles * Math.sqrt(c.molecularWeight);

    totalMass += massFlowKgH;
    totalMoles += moles;
    molarCpSum += moles * c.idealGasCpJKmolK;
    viscNum += weight * c.viscosityCp;
    viscDen += weight;
  }

  if (totalMass <= 0 || totalMoles <= 0) return null;

  const molecularWeight = totalMass / totalMoles;
  const molarCpMix = molarCpSum / totalMoles; // J/(kmol·K)
  // [J/(kmol·K)] ÷ [kg/kmol] = J/(kg·K) — 단위가 그대로 약분된다(×1000 아님).
  const specificHeatJKgK = molarCpMix / molecularWeight;

  return {
    molecularWeight,
    specificHeatJKgK,
    specificHeatRatio: molarCpMix / (molarCpMix - R_UNIVERSAL),
    viscosityCp: viscNum / viscDen,
    totalMassFlowKgH: totalMass,
  };
}

/** MJ/kg → kcal/kg 환산계수. KOSHA GUIDE C-C-18-2026 계산 엑셀이 실제로 쓰는
 * 값(239.006*MJ/kg)과 같다 — `koshaFlareRadiation.ts` 의 heatingValueKcalKg
 * 단위와 맞추기 위해 여기서 바로 kcal/kg 로 낸다. */
const MJ_PER_KG_TO_KCAL_PER_KG = 239.006;

export interface FlareGasMixProperties {
  readonly molecularWeight: number;
  /** 보정 전 폭발하한 농도계수 CL' — Le Chatelier 식(KOSHA GUIDE C-C-18-2026 식 9). */
  readonly lelPrime: number;
  /** 연소열량(HHV 기준), kcal/kg — `koshaFlareRadiation.ts` 의 heatingValueKcalKg 입력과 같은 단위. */
  readonly heatingValueKcalKg: number;
  readonly totalMassFlowKgH: number;
}

/**
 * 성분별 질량유량(kg/h) 딕셔너리에서 플레어 계산용 혼합 물성(분자량·CL'·
 * 연소열량)을 구한다. `mixComponentProperties()` 와 별도인 이유는 필요한
 * 성분 물성이 다르기 때문이다(Cp·점도 대신 폭발하한계·연소열량).
 *
 * 분자량·CL'(=LEL) 은 `explosiveLimits.ts` 의 `mixExplosiveLimits()`(KOSHA
 * GUIDE P-179-2022/ISO 10156 질소등가계수 보정)를 그대로 재사용한다 —
 * "혼합가스 폭발한계 계산기" 탭과 계산 로직을 공유한다. 연소열량만 이
 * 함수 고유로 더한다:
 *   연소열량 : HHVmix = Σ(massᵢ·HHVᵢ) / Σmassᵢ — 질량기준 가중평균(불연성
 *            성분은 HHVᵢ=0 으로 취급, 분모에는 포함돼 전체 유량 기준으로
 *            희석된다)
 *
 * 알 수 없는 성분 key 가 있거나, 유량 합이 0 이거나, 가연성 성분이 하나도
 * 없어 CL' 을 구할 수 없거나, 질소등가계수를 모르는 불연성 성분(염소 등)이
 * 섞여 있으면(추측해서 채우지 않는다) null.
 */
export function mixFlareGasProperties(
  components: Readonly<Record<string, number>>,
  customDefs?: Readonly<Record<string, GasComponent>>,
): FlareGasMixProperties | null {
  const limits = mixExplosiveLimits(components, customDefs);
  if (limits === null) return null;

  let hhvMassSum = 0;
  for (const [key, massFlowKgH] of Object.entries(components)) {
    if (!Number.isFinite(massFlowKgH) || massFlowKgH <= 0) continue;
    const c = customDefs?.[key] ?? findGasComponent(key)!;
    if (c.hhvMjKg !== null) hhvMassSum += massFlowKgH * c.hhvMjKg;
  }

  return {
    molecularWeight: limits.molecularWeight,
    lelPrime: limits.lelVolPercent,
    heatingValueKcalKg: (hhvMassSum / limits.totalMassFlowKgH) * MJ_PER_KG_TO_KCAL_PER_KG,
    totalMassFlowKgH: limits.totalMassFlowKgH,
  };
}

/**
 * 성분 몰% 목록 + 총 건가스 질량유량(kg/h) → 성분별 질량유량 딕셔너리.
 *
 * 몰분율 yᵢ 인 성분을 질량기준으로 나누면: massᵢ = M_total · (yᵢ·MWᵢ) / Σ(yⱼ·MWⱼ)
 * (몰%는 합이 100 이 아니어도 상대 비율로 정규화해서 쓴다 — 사용자가 입력
 * 중일 때 100 을 잠깐 벗어나도 화면이 깨지지 않게 하기 위해서다. 다만 몰%
 * 합이 100에서 크게 벗어나면 호출부(화면)에서 별도로 경고해야 한다.)
 *
 * 알 수 없는 성분 key 가 있거나, 몰%·질량유량이 전부 유효하지 않으면 null.
 */
export function moleFractionsToMassComponents(
  totalMassFlowKgH: number,
  entries: ReadonlyArray<{ readonly key: string; readonly molePercent: number }>,
): Readonly<Record<string, number>> | null {
  if (!Number.isFinite(totalMassFlowKgH) || totalMassFlowKgH <= 0) return null;

  let weightSum = 0;
  const weights: Record<string, number> = {};
  for (const e of entries) {
    if (!Number.isFinite(e.molePercent) || e.molePercent <= 0) continue;
    const c = findGasComponent(e.key);
    if (c === null) return null;
    const w = e.molePercent * c.molecularWeight;
    weights[e.key] = (weights[e.key] ?? 0) + w;
    weightSum += w;
  }
  if (weightSum <= 0) return null;

  const out: Record<string, number> = {};
  for (const [key, w] of Object.entries(weights)) {
    out[key] = (totalMassFlowKgH * w) / weightSum;
  }
  return out;
}
